import { useState, useCallback } from 'react';
import { useMoveModuleToNewRowMutation, useUpdatePageModuleMutation } from '../editCourseApi';
import { findModuleInLayout, getRowIndexById } from '../utils/layoutUtils';

/**
 * Hook for managing module layout changes in the organizer modal
 * Saves immediately on drop (same pattern as DraggableCoursePage)
 * @param {Object} options
 * @param {Array} options.pageRows - Current page rows from API
 * @param {number} options.pageId - Current page ID
 * @param {Function} options.onSaveSuccess - Callback on successful save
 * @param {Function} options.onSaveError - Callback on save error
 * @returns {Object} - Layout organizer state and handlers
 */
export const useLayoutOrganizer = ({ pageRows, pageId, onSaveSuccess, onSaveError }) => {
  const [isSaving, setIsSaving] = useState(false);

  const [moveModuleToNewRow] = useMoveModuleToNewRowMutation();
  const [updatePageModule] = useUpdatePageModuleMutation();

  // Handle drop and save immediately (like DraggableCoursePage)
  const handleDrop = useCallback(
    async (dropData) => {
      setIsSaving(true);

      try {
        if (dropData.type === 'vertical') {
          // Vertical drop: Move module to a new row at specific position
          // API expects 1-based pageRowPosition
          const pageRowPosition = dropData.targetPosition + 1;

          await moveModuleToNewRow({
            moduleId: dropData.moduleId,
            pageRowPosition: pageRowPosition,
            pageId: pageId,
          }).unwrap();

          if (onSaveSuccess) onSaveSuccess();
        } else if (dropData.type === 'horizontal') {
          // Horizontal drop: Move module to existing row at specific position
          const targetRowIndex = getRowIndexById(pageRows, dropData.targetRowId);
          if (targetRowIndex === -1) {
            throw new Error('Target row not found');
          }

          const targetRow = pageRows[targetRowIndex];

          // Build new module order for the target row
          const targetModules = [...targetRow.pageModules];

          // Find the dragged module info
          const found = findModuleInLayout(pageRows, dropData.moduleId);
          if (!found) {
            throw new Error('Dragged module not found');
          }

          // Remove dragged module if it's already in the target row
          const draggedModuleIndexInTarget = targetModules.findIndex((m) => m.id === dropData.moduleId);
          if (draggedModuleIndexInTarget !== -1) {
            targetModules.splice(draggedModuleIndexInTarget, 1);
          }

          // Calculate insert index
          let insertIndex;
          if (dropData.targetPosition === 'left') {
            insertIndex = 0;
          } else if (dropData.targetPosition === 'center') {
            insertIndex = 1;
          } else {
            // 'right'
            insertIndex = targetModules.length;
          }

          // Insert dragged module at the correct position
          targetModules.splice(insertIndex, 0, found.module);

          // Update ALL modules in the target row with their new sortOrder
          const updatePromises = targetModules.map((module, index) =>
            updatePageModule({
              moduleId: module.id,
              pageId: pageId,
              pageRowId: targetRow.id,
              sortOrder: index + 1, // 1-based sortOrder
            }).unwrap()
          );

          await Promise.all(updatePromises);

          if (onSaveSuccess) onSaveSuccess();
        }
      } catch (error) {
        console.error('Failed to move module:', error);
        if (onSaveError) onSaveError(error);
      } finally {
        setIsSaving(false);
      }
    },
    [pageRows, pageId, moveModuleToNewRow, updatePageModule, onSaveSuccess, onSaveError]
  );

  return {
    localRows: pageRows, // Use pageRows directly since RTK Query will update it
    isSaving,
    handleDrop,
  };
};
